import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import cn from 'classnames';

import type { TimeFormat } from '@app/api/Branches/types';

import { useCalendarTimeline } from '../hooks';

import type { FC } from 'react';

import '../styles/CalendarTimeline.scss';

interface CalendarTimelineProps {
  side: 'left' | 'right';
  startTime: string;
  endTime: string;
  boundaryEndTime?: string;
  stepMinutes?: number;
  onNavigate: () => void;
  disabled?: boolean;
  navigationAriaLabel?: string;
  timeFormat?: TimeFormat | null;
}

export const CalendarTimeline: FC<CalendarTimelineProps> = ({
  side,
  startTime,
  endTime,
  boundaryEndTime,
  stepMinutes = 15,
  onNavigate,
  disabled = false,
  navigationAriaLabel,
  timeFormat,
}) => {
  const { boundaryEndMark, timelineMarks } = useCalendarTimeline({
    startTime,
    endTime,
    boundaryEndTime,
    stepMinutes,
    timeFormat,
  });

  const isLeft = side === 'left';
  const ArrowIcon = isLeft ? LeftOutlined : RightOutlined;

  return (
    <div
      className={`CalendarTimeline CalendarTimeline--${side}`}
      data-testid={`calendar-timeline-${side}`}
    >
      <div className="CalendarTimeline__header">
        <button
          className="CalendarTimeline__nav-button"
          onClick={onNavigate}
          disabled={disabled}
          aria-label={
            navigationAriaLabel ||
            (isLeft
              ? 'Предыдущая страница исполнителей'
              : 'Следующая страница исполнителей')
          }
          data-testid={`calendar-timeline-nav-${side}`}
        >
          <ArrowIcon />
        </button>
      </div>

      <div className="CalendarTimeline__scale">
        {timelineMarks.map(({ time, label, kind, type, isAnchorLabel }) => (
          <div
            key={`${kind}-${time}`}
            className={cn(
              'CalendarTimeline__mark',
              `CalendarTimeline__mark--${type}`,
              `CalendarTimeline__mark--${kind}`,
              {
                'CalendarTimeline__mark--anchor-label': isAnchorLabel,
              }
            )}
            data-testid={`calendar-timeline-mark-${time}`}
          >
            {label && <span className="CalendarTimeline__label">{label}</span>}
            <span className="CalendarTimeline__tick" />
          </div>
        ))}
        {boundaryEndMark && (
          <div
            className={cn(
              'CalendarTimeline__mark',
              `CalendarTimeline__mark--${boundaryEndMark.type}`,
              'CalendarTimeline__mark--boundary-end',
              {
                'CalendarTimeline__mark--anchor-label':
                  boundaryEndMark.type !== 'hour',
              }
            )}
            style={{
              top: `${boundaryEndMark.offsetPercent}%`,
            }}
            data-testid={`calendar-timeline-mark-boundary-end-${boundaryEndMark.time}`}
          >
            <span className="CalendarTimeline__label">
              {boundaryEndMark.label}
            </span>
            <span className="CalendarTimeline__tick" />
          </div>
        )}
      </div>
    </div>
  );
};
